import React, { useState, useEffect, useMemo } from 'react';
import { useNetworkContext } from '@/contexts/NetworkContext';
import { Connection, FileType } from '@/types/data';
import { DataGrid, type Column, textEditor, type SortColumn } from 'react-data-grid';
import { useConnectionsData } from '@/hooks/useConnectionsData';
// Using direct path references to SVG files in the public directory
const trashIcon = '/trash.svg';
const saveIcon = '/save.svg';
const infoIcon = '/info.svg';
import { EmptyState } from '@/components/common/EmptyState';
import { ConnectionsHeader } from '@/components/RightPanel/ConnectionsHeader';
import { AutosaveIndicator } from '@/components/common/AutosaveIndicator';

const ConnectionsTab: React.FC = () => {
  const { currentNetwork } = useNetworkContext();
  const {
    connections,
    isEdited,
    isSaving,
    addEmptyRow,
    discardChanges,
    handleRowsChange,
    deleteRow,
  } = useConnectionsData(currentNetwork);
  const [isLoading, setIsLoading] = useState(true);
  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);

  // Set loading state when network changes
  useEffect(() => {
    setIsLoading(true);
    // Short timeout to simulate loading and prevent flickering
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [currentNetwork]);

  // Get comparator function for sorting
  const getComparator = (sortColumn: string): ((a: Connection, b: Connection) => number) => {
    // All connection properties are strings, so we can use localeCompare for all columns
    return (a, b) => {
      const aValue = a[sortColumn as keyof Connection];
      const bValue = b[sortColumn as keyof Connection];

      // Handle optional properties (like account)
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return -1;
      if (bValue === undefined) return 1;

      return String(aValue).localeCompare(String(bValue));
    };
  };

  // Apply sorting to rows
  const sortedRows = useMemo((): readonly Connection[] => {
    if (sortColumns.length === 0) return connections;

    return [...connections].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [connections, sortColumns]);

  // Define columns for the data grid
  const columns: Column<Connection>[] = [
    {
      key: 'courier',
      name: 'Courier',
      sortable: true,
      renderEditCell: textEditor,
      width: 100,
      resizable: true,
    },
    {
      key: 'start',
      name: 'Start',
      sortable: true,
      renderEditCell: textEditor,
      width: 100,
      resizable: true,
    },
    {
      key: 'end',
      name: 'End',
      sortable: true,
      renderEditCell: textEditor,
      width: 100,
      resizable: true,
    },
    {
      key: 'zone',
      name: 'Zone',
      sortable: true,
      renderEditCell: textEditor,
      width: 80,
      resizable: true,
    },
    {
      key: 'account',
      name: 'Account',
      sortable: true,
      renderEditCell: textEditor,
      width: 100,
      resizable: true,
    },
    {
      key: 'priority',
      name: 'Priority',
      sortable: true,
      renderEditCell: textEditor,
      width: 80,
      resizable: true,
    },
    {
      key: 'book_on_scan',
      name: 'Book on Scan',
      sortable: true,
      renderEditCell: textEditor,
      width: 120,
      resizable: true,
    },
    {
      key: 'book_on_booking',
      name: 'Book on Booking',
      sortable: true,
      renderEditCell: textEditor,
      width: 140,
      resizable: true,
    },
    {
      key: 'book_on_tracking_from_previous_leg',
      name: 'Book on Tracking',
      sortable: true,
      renderEditCell: textEditor,
      width: 140,
      resizable: true,
    },
    {
      key: 'delete',
      name: '',
      width: 50,
      renderCell: ({ rowIdx }) => (
        <button
          onClick={() => deleteRow(rowIdx)}
          className="text-red-600 hover:text-red-800 p-1 transition-colors"
          title="Delete this connection"
        >
          <img src={trashIcon} alt="Delete" className="h-5 w-5" />
        </button>
      ),
    },
  ];

  if (!currentNetwork) {
    return (
      <EmptyState
        message="No network selected"
        subMessage="Please select a network from the left panel to view and edit connections."
      />
    );
  }

  const connectionsFile = currentNetwork.files.find((file) => file.type === FileType.Connections);

  if (!connectionsFile) {
    return (
      <EmptyState
        message="No connections file found"
        subMessage="The selected network does not contain a connections file. Please select a different network or add a connections file to this network."
      />
    );
  }

  if (isLoading) {
    return (
      <div className="py-4 flex justify-center items-center h-64">
        <div className="animate-pulse text-indigo-600">Loading connections data...</div>
      </div>
    );
  }

  return (
    <div className="py-4">
      <ConnectionsHeader
        addEmptyRow={addEmptyRow}
        discardChanges={discardChanges}
        isEdited={isEdited}
      >
        <AutosaveIndicator isSaving={isSaving} isEdited={isEdited} />
      </ConnectionsHeader>

      <div className="border rounded overflow-hidden shadow-sm mb-4">
        <DataGrid
          columns={columns}
          rows={sortedRows}
          onRowsChange={handleRowsChange}
          className="rdg-light"
          style={{ height: 'calc(100vh - 250px)', minHeight: '400px' }}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
        />
      </div>

      <div
        className={`text-sm p-2 rounded ${isEdited ? 'bg-indigo-50 text-indigo-700' : 'text-gray-500'}`}
      >
        {isEdited ? (
          <div className="flex items-center">
            <img src={saveIcon} alt="Save" className="h-5 w-5" />
            <span className="ml-1">
              Your changes are automatically saved to local storage and will be applied when you
              export the network
            </span>
          </div>
        ) : (
          <div className="flex items-center">
            <img src={infoIcon} alt="Info" className="h-5 w-5" />
            <span className="ml-1">Changes will be automatically saved as you edit</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectionsTab;
